import { api } from './api';
import type { Pagination } from './group.service';
import type { UserRef } from './goal.service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface ColumnLeader {
  id: string;
  user: UserRef;
}

export interface SprintColumn {
  id: string;
  name: string;
  display_order: number;
  leaders: ColumnLeader[];
  task_count: number;
}

export interface SprintMember {
  id: string;
  role: string;
  user: {
    id: string;
    full_name: string;
    email: string;
    avatar_url: string | null;
    working_days?: string[];
    daily_hours?: number;
  };
  created_at: string;
}

export interface SprintGoal {
  id: string;
  title: string;
  description: string | null;
  display_order: number;
  business_ready: boolean;
  development_done: boolean;
  testing_completed: boolean;
  uat_approved: boolean;
  created_at: string;
}

export interface SprintStats {
  total_tasks: number;
  done_tasks: number;
  in_progress_tasks: number;
  not_started_tasks: number;
}

export interface Sprint {
  id: string;
  reference_id: string;
  name: string;
  description: string | null;
  status: string;
  start_date: string;
  end_date: string;
  evaluation_score: number | null;
  completion_summary: string | null;
  owner_id: string;
  group_id: string;
  activated_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  owner?: UserRef;
  group?: { id: string; name: string; reference_id: string };
}

export interface SprintDetail extends Sprint {
  columns: SprintColumn[];
  members: SprintMember[];
  evaluators: { id: string; user: UserRef }[];
  sprint_goals: SprintGoal[];
  stats: SprintStats;
  is_live: boolean;
  is_late: boolean;
}

export interface SprintGoalWithTasks extends SprintGoal {
  total_tasks: number;
  completed_tasks: number;
  tasks?: Array<{
    id: string;
    reference_id: string;
    title: string;
    column_id: string | null;
    column_name: string | null;
    column_order: number | null;
    phase_status: string;
    calculated_status: string;
    assignee_name: string | null;
    end_date: string | null;
  }>;
}

export interface SprintListParams {
  cursor?: string;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  status?: string;
  group_id?: string;
}

export interface CreateSprintData {
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  group_id: string;
  column_leaders?: { column_name: string; leader_id: string }[];
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------
export const sprintService = {
  list: (params?: SprintListParams) =>
    api.get<{ success: boolean; data: Sprint[]; pagination: Pagination }>('/sprints', { params }),

  get: (id: string) =>
    api.get<{ success: boolean; data: SprintDetail }>(`/sprints/${id}`),

  create: (data: CreateSprintData) =>
    api.post<{ success: boolean; data: Sprint }>('/sprints', data),

  update: (id: string, data: Partial<Pick<Sprint, 'name' | 'description' | 'start_date' | 'end_date'>>) =>
    api.patch<{ success: boolean; data: Sprint }>(`/sprints/${id}`, data),

  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/sprints/${id}`),

  // Status transitions
  activate: (id: string) =>
    api.post(`/sprints/${id}/activate`),
  complete: (id: string, completion_summary?: string) =>
    api.post(`/sprints/${id}/complete`, { completion_summary }),
  cancel: (id: string) =>
    api.post(`/sprints/${id}/cancel`),

  // Members
  addMember: (id: string, data: { user_id: string; role?: string }) =>
    api.post<{ success: boolean; data: SprintMember }>(`/sprints/${id}/members`, data),
  removeMember: (id: string, memberId: string) =>
    api.delete(`/sprints/${id}/members/${memberId}`),

  // Sprint Goals
  createGoal: (id: string, data: { title: string; description?: string }) =>
    api.post<{ success: boolean; data: SprintGoal }>(`/sprints/${id}/goals`, data),
  updateGoal: (id: string, sgId: string, data: Partial<SprintGoal>) =>
    api.patch<{ success: boolean; data: SprintGoal }>(`/sprints/${id}/goals/${sgId}`, data),
  deleteGoal: (id: string, sgId: string) =>
    api.delete(`/sprints/${id}/goals/${sgId}`),
  reorderGoals: (id: string, goal_ids: string[]) =>
    api.patch(`/sprints/${id}/goals/reorder`, { goal_ids }),
  moveGoal: (id: string, sgId: string, data: { target_sprint_id: string; move_tasks: boolean }) =>
    api.post(`/sprints/${id}/goals/${sgId}/move`, data),

  // Columns
  addColumn: (id: string, data: { name: string }) =>
    api.post(`/sprints/${id}/columns`, data),
  updateColumn: (id: string, colId: string, name: string) =>
    api.patch(`/sprints/${id}/columns/${colId}`, { name }),
  deleteColumn: (id: string, colId: string) =>
    api.delete(`/sprints/${id}/columns/${colId}`),
  reorderColumns: (id: string, column_ids: string[]) =>
    api.patch(`/sprints/${id}/columns/reorder`, { column_ids }),

  // Column Leaders
  addColumnLeader: (id: string, colId: string, userId: string) =>
    api.post(`/sprints/${id}/columns/${colId}/leaders`, { user_id: userId }),
  removeColumnLeader: (id: string, colId: string, leaderId: string) =>
    api.delete(`/sprints/${id}/columns/${colId}/leaders/${leaderId}`),

  // Evaluators
  addEvaluator: (id: string, userId: string) =>
    api.post(`/sprints/${id}/evaluators`, { user_id: userId }),
  removeEvaluator: (id: string, evalId: string) =>
    api.delete(`/sprints/${id}/evaluators/${evalId}`),

  // Member role update
  updateMemberRole: (id: string, memberId: string, role: string) =>
    api.patch(`/sprints/${id}/members/${memberId}`, { role }),

  // Goals with tasks
  getGoalsWithTasks: (id: string) =>
    api.get<{ success: boolean; data: SprintGoalWithTasks[] }>(`/sprints/${id}/goals-with-tasks`),

  // Tasks
  listTasks: (id: string, params?: Record<string, string>) =>
    api.get(`/sprints/${id}/tasks`, { params }),

  // Attachments (generic - works for sprint_goal entity_type)
  listAttachments: (entityType: string, entityId: string) =>
    api.get<{ success: boolean; data: import('./task.service').Attachment[] }>(`/attachments/${entityType}/${entityId}`),
  uploadAttachment: (entityType: string, entityId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    return api.post<{ success: boolean; data: import('./task.service').Attachment }>(
      '/attachments/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  addAttachmentUrl: (entityType: string, entityId: string, url: string, fileName?: string) =>
    api.post<{ success: boolean; data: import('./task.service').Attachment }>('/attachments/url', {
      entity_type: entityType,
      entity_id: entityId,
      url,
      file_name: fileName,
    }),
  downloadAttachment: (attId: string) =>
    api.get(`/attachments/${attId}/download`, { responseType: 'blob' }),
  deleteAttachment: (attId: string) =>
    api.delete(`/attachments/${attId}`),
};
